import {Component, EventEmitter, Input, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';

import {DictService} from '@core/ecv-http-services/services/dict.service';
import {ExperienceService} from '@core/ecv-http-services/services/experience.service';
import {SkillService} from '@core/ecv-http-services/services/skill.service';
import {DialogService} from '../dialog/dialog.service';
import {DictModel} from '../dictionary/dict.model';
import {StepComponentAbstract} from '../flow/step.component';
import {CustomValidators} from '@core/validator/custom-validators';
import {ValidatorService} from '@core/validator/validator.service';
import {ExperienceItemModel} from './experience-item.model';
import {ExpertExperienceModel} from './expert-experience.model';
import {SkillsModel} from './skills.model';
import {DictType} from '../enums/dict-types.enum';
import {DictEnum} from '../enums/dict.enum';
import {EcvLanguageService} from '@core/ecv-language.service';
import DateUtils from '../shared/date-utils';
import {isNullOrUndefined} from 'util';

@Component({
  templateUrl: './experience.component.html'
})
export class ExperienceComponent extends StepComponentAbstract implements OnInit {

  @Input() data: any;
  expertExperience: ExpertExperienceModel;
  skillsModel: SkillsModel;
  skillsDict: DictModel[] = [];
  userId: number;
  currentSkill: number;
  DictType = DictType;
  pendingDict: number[] = [];
  isSaveInProgress: boolean;
  DictEnum = DictEnum;
  modalEmitter = new EventEmitter();

  constructor(private experienceService: ExperienceService, private skillsService: SkillService,
              private validatorService: ValidatorService, private dictService: DictService,
              private langService: EcvLanguageService,
              private dialogService: DialogService) {
    super();
    this.skillsModel = new SkillsModel;
    this.expertExperience = new ExpertExperienceModel;
    this.isSaveInProgress = false;
  }

  ngOnInit(): void {
    this.createForm();
  }

  addExperience() {
    const newItem: ExperienceItemModel = new ExperienceItemModel();
    this.expertExperience.experienceItems.push(newItem);
  }

  removeExperience(index: number) {
    this.expertExperience.experienceItems[index].deleted = true;
  }

  addSkill() {
    if (!(this.skillsModel.skills.lastIndexOf(this.currentSkill) > -1) && !isNullOrUndefined(this.currentSkill)) {
      this.skillsModel.skills.unshift(this.currentSkill);
    }
    this.currentSkill = undefined;
  }

  removeSkill(index: number) {
    this.skillsModel.skills.splice(index, 1);
  }

  getSkillName(id: number): string {
    if (!id) {
      return '';
    }
    const foundSkill = this.skillsDict.find(x => x.id === id);
    if (!foundSkill) {
      if (this.pendingDict.find(x => x === id)) {
        return '';
      }
      this.pendingDict.push(id);
      this.dictService.getDictionary(id).subscribe(response => {
        if (!this.skillsDict.find(x => x.id === id)) {
          this.skillsDict.push(response);
        }
      });
      return '';
    }
    return this.langService.pickFromDictionary(foundSkill);
  }

  private createForm() {
    this.stepForm = new FormGroup({
      'skill': new FormControl(''),
      'skill-id': new FormControl('', CustomValidators.validateCorrectDict)
    });
  }

  onSave(): Observable<any> {
    if (this.isSaveInProgress) {
      return Observable.of(true);
    }
    for (let i = 0; i < this.expertExperience.experienceItems.length; i++) {
      this.expertExperience.experienceItems[i].duties = this.expertExperience.experienceItems[i].duties
        ? this.expertExperience.experienceItems[i].duties.trim() : null;
      this.expertExperience.experienceItems[i].dutiesEn = this.expertExperience.experienceItems[i].dutiesEn
        ? this.expertExperience.experienceItems[i].dutiesEn.trim() : null;
      this.expertExperience.experienceItems[i].startWorkDate.dateFormat =
        DateUtils.parseDateFormat(this.expertExperience.experienceItems[i].startWorkDate.date);
      this.expertExperience.experienceItems[i].endWorkDate.dateFormat =
        DateUtils.parseDateFormat(this.expertExperience.experienceItems[i].endWorkDate.date);
    }
    this.validatorService.validate(this.stepForm);
    if (!this.validatorService.isValid(this.stepForm)) {
      return Observable.of(false);
    }

    if (!this.validPositionsInAllExperienceItems()) {
      return Observable.of(false);
    }

    const changes: Observable<any>[] = this.updateChanges();
    if (changes.length === 0) {
      return Observable.of(true);
    } else {
      this.isSaveInProgress = true;
      return Observable.forkJoin(changes)
        .map(() => {
          this.isSaveInProgress = false;
        }, () => {
          this.isSaveInProgress = false;
        });
    }
  }

  updateChanges(): Observable<any>[] {
    return [
      this.deleteMarkedExperienceItems(),
      this.saveExperienceItems(),
      this.updateExperienceItems(),
      this.skillsService.updateSkills(this.skillsModel)
    ];
  }

  private validPositionsInAllExperienceItems(): boolean {
    const items = this.expertExperience.experienceItems.filter(value => !value.deleted);
    for (let i = 0; i < items.length; i++) {
      if (items[i].positionDictIds.length < 1) {
        return false;
      }
    }
    return true;
  }

  loadData(userId): void {
    this.userId = userId;

    this.experienceService.getAllExperiences(userId).subscribe(
      response => {
        this.expertExperience = response;
      });

    this.skillsService.getSkills(userId).subscribe(
      response => {
        this.skillsModel.skills = response;
      });
    this.skillsModel.userId = userId;
  }

  private deleteMarkedExperienceItems(): Observable<any> {
    const experienceIds = this.expertExperience.experienceItems
      .filter(experience => experience.id && experience.deleted).map(experience => experience.id);
    if (!experienceIds.length) {
      return Observable.of(true);
    }
    return this.experienceService.deleteExperience(experienceIds)
      .map(() => this.expertExperience.experienceItems =
        this.expertExperience.experienceItems.filter(experience => experienceIds.indexOf(experience.id) === -1))
      .catch((err) => Observable.of(true));
  }

  private saveExperienceItems(): Observable<any> {
    const experienceItemsToNotToCreate = this.expertExperience.experienceItems
      .filter(experience => !experience.deleted && experience.id);
    const experienceItemsToCreate = this.expertExperience.experienceItems
      .filter(experience => !experience.deleted && !experience.id);
    if (!experienceItemsToCreate.length) {
      return Observable.of(true);
    }
    const payload = {
      userId: this.userId,
      newExperiences: experienceItemsToCreate
    };
    return this.experienceService.createExperience(payload)
      .map((res) => this.expertExperience.experienceItems = [...experienceItemsToNotToCreate, ...res.newExperiences])
      .catch((err) => Observable.of(true));
  }

  private updateExperienceItems(): Observable<any> {
    const experienceItemsToUpdate = this.expertExperience.experienceItems
      .filter(experience => !experience.deleted && experience.id);
    if (!experienceItemsToUpdate.length) {
      return Observable.of(true);
    }
    return this.experienceService.updateExperience(experienceItemsToUpdate)
      .map((res) => {
        experienceItemsToUpdate.forEach(experience => {
          const updatedExperience = res.find(elem => elem.id === experience.id);
          Object.assign(experience, updatedExperience);
        });
      })
      .catch((err) => Observable.of(true));
  }

  openModal(title: string, dictType: number, control: AbstractControl, maxLength: number) {
    this.dialogService.open(title, dictType, control, maxLength, this, 'currentSkill', this.modalEmitter);
    this.modalEmitter.subscribe((element) => {
      this.stepForm.controls['skill-id'].setValue(element.id);
      this.addSkill();
    });
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/experience/experience.component.ts